import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Users, TrendingUp, CheckCircle, ArrowLeft, Zap, MessageSquare } from 'lucide-react';

const CampaignSolutions = () => {
  return (
    <div className="dark-theme pt-24">
      {/* Breadcrumb */}
      <section className="py-8 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-2 text-gray-400">
            <Link to="/solutions" className="hover:text-bluemp-400 transition-colors">Solutions</Link>
            <span>/</span>
            <span className="text-white">AI Campaign Solutions</span>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link to="/solutions" className="inline-flex items-center gap-2 text-bluemp-400 hover:text-bluemp-300 transition-colors mb-6">
                <ArrowLeft size={16} />
                Back to Solutions
              </Link>
              
              <h1 className="display-large text-white mb-6">
                Smarter Campaigns.{' '}
                <span className="text-bluemp-400">Stronger Connections.</span>
              </h1>
              
              <p className="body-large text-gray-300 mb-8">
                Precision targeting and intelligent engagement that moves you from broad marketing to personalized customer connections. Create campaigns that resonate, engage, and convert with AI-driven insights.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary">
                  Optimize Your Campaigns
                  <ArrowRight size={20} />
                </Link>
                <Link to="/experience" className="btn-secondary">
                  View Campaign Results
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20">
                <Target size={64} className="text-bluemp-400 mb-6" />
                <h3 className="heading-2 text-white mb-4">Intelligent Campaign Engine</h3>
                <p className="body-medium text-gray-300 mb-6">
                  Transform your marketing approach with AI-powered segmentation, predictive modeling, and automated optimization for maximum impact.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-bluemp-400/10 rounded">
                    <div className="heading-3 text-bluemp-400">60%</div>
                    <div className="body-small text-gray-300">Higher ROI</div>
                  </div>
                  <div className="text-center p-3 bg-bluemp-400/10 rounded">
                    <div className="heading-3 text-bluemp-400">45%</div>
                    <div className="body-small text-gray-300">Better Conversion</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Core Capabilities
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Advanced AI technologies that transform your marketing campaigns into precision-targeted, highly effective customer engagement tools.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: Users,
                title: "AI-Driven Customer Segmentation",
                description: "Intelligent algorithms identify customer segments based on behavior, preferences, and likelihood to engage."
              },
              {
                icon: TrendingUp,
                title: "Predictive Modeling & Scoring", 
                description: "Advanced models predict campaign success probability and optimal customer journey paths."
              },
              {
                icon: Zap,
                title: "Automated Channel Optimization",
                description: "Real-time optimization of communication channels, timing, and messaging for maximum effectiveness."
              },
              {
                icon: MessageSquare,
                title: "Personalized Content Generation",
                description: "AI-powered content creation that delivers personalized messages at scale while maintaining brand consistency."
              }
            ].map((capability, index) => (
              <div key={index} className="group dark-hover bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20">
                <capability.icon size={48} className="text-bluemp-400 mb-6" />
                <h3 className="heading-2 text-white mb-4 group-hover:text-bluemp-400 transition-colors">
                  {capability.title}
                </h3>
                <p className="body-medium text-gray-300">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="display-medium text-white mb-6">
            Ready to Transform Your Marketing Campaigns?
          </h2>
          <p className="body-large text-gray-300 mb-8">
            Create campaigns that connect with customers on a personal level while delivering measurable business results through AI-driven intelligence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Start Smart Campaigns
              <ArrowRight size={20} />
            </Link>
            <Link to="/solutions/unclaimed-funds" className="btn-secondary">
              Explore Unclaimed Funds
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CampaignSolutions;